import { useEffect, useMemo, useState } from 'react';
import Drawer from '../../components/Drawer';
import Spinner from '../../components/Spinner';
import { fosApi, fosError, fosFunds, formatMoney, parseMoney, scopeFromKey, todayLocal, type DirectoryRow, type LedgerView, type PledgeView } from '../../lib/fosApi';
import { useFinancialGuard } from './useFinancial';
import { Avatar, ErrorNote, Icon, useDebounced } from './ui';

export type ContributionMode = { kind: 'pledge' } | { kind: 'gift'; pledgeId?: string };

/** Pick a donor by name (only donors the caller may see are searchable). */
function DonorPicker({ onPick }: { onPick: (d: { id: string; name: string }) => void }) {
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 250);
  const [rows, setRows] = useState<DirectoryRow[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (debounced.trim().length < 2) { setRows([]); return; }
    let cancelled = false; setBusy(true);
    fosApi.list({ q: debounced.trim(), limit: 8 }).then((p) => { if (!cancelled) setRows(p.rows); }).catch(() => { if (!cancelled) setRows([]); }).finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [debounced]);
  return (
    <div className="fos-form">
      <label>Donor<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or reference…" autoFocus /></label>
      {busy && <Spinner label="Searching" />}
      <ul className="fos-pick-list">
        {rows.map((r) => (
          <li key={r.donorId}><button type="button" className="fos-pick" onClick={() => onPick({ id: r.donorId, name: r.displayName })}><Avatar name={r.displayName} size={30} /><span><strong>{r.displayName}</strong><small>{r.ref}</small></span></button></li>
        ))}
        {!busy && debounced.trim().length >= 2 && rows.length === 0 && <li className="fos-hint">No matching donor you can work with.</li>}
      </ul>
    </div>
  );
}

function Form({ ledger, mode, onClose, onSaved }: { ledger: LedgerView; mode: ContributionMode; onClose: () => void; onSaved: () => void }) {
  const isGift = mode.kind === 'gift';
  const { guard, modal } = useFinancialGuard();
  const openPledges = useMemo(() => ledger.pledges.filter((p) => p.status === 'active' && p.outstandingCents > 0 && ledger.paymentScopes.some((s) => s.scopeKey === p.scopeKey)), [ledger]);
  const scopes = isGift ? ledger.paymentScopes : ledger.pledgeScopes;
  const [pledgeId, setPledgeId] = useState(isGift ? (mode.pledgeId ?? '') : '');
  const pledge: PledgeView | undefined = openPledges.find((p) => p.id === pledgeId);
  const [scopeKey, setScopeKey] = useState(scopes[0]?.scopeKey ?? '');
  const primary = ledger.campaigns.find((c) => c.primary) ?? ledger.campaigns[0];
  const [campaignKey, setCampaignKey] = useState(primary?.key ?? '');
  const campaign = ledger.campaigns.find((c) => c.key === campaignKey);
  const openYears = (campaign?.years ?? []).filter((y) => y.open).map((y) => y.year);
  const [campaignYear, setCampaignYear] = useState('');
  const [amount, setAmount] = useState(pledge ? String(pledge.outstandingCents / 100) : '');
  const [received, setReceived] = useState(todayLocal());
  const [confirmNow, setConfirmNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (!openYears.includes(campaignYear)) setCampaignYear(openYears.find((y) => y === String(new Date().getFullYear())) ?? openYears[0] ?? ''); }, [campaignKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (pledge) setAmount(String(pledge.outstandingCents / 100)); }, [pledgeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cents = parseMoney(amount);
  const effScopeKey = pledge ? pledge.scopeKey : scopeKey;
  const problem = !cents ? 'Enter an amount, like 250 or 1,250.50.'
    : pledge && cents > pledge.outstandingCents ? `That is more than the pledge's remaining ${formatMoney(pledge.outstandingCents)}.`
    : !effScopeKey ? 'You cannot record this for any of the donor\'s relationships.'
    : !pledge && !campaignYear ? 'This campaign has no open campaign year.'
    : isGift && !received ? 'Choose the date it was received.' : '';

  const submit = async () => {
    if (problem || !cents) { setError(problem); return; }
    setBusy(true); setError('');
    try {
      await guard(() => (isGift
        ? fosFunds.mutate({ operation: 'payment.record', donorId: ledger.donor.id, amountCents: cents, receivedDate: received, ...(confirmNow ? { confirm: true } : {}),
          ...(pledge ? { pledgeId: pledge.id } : { scope: scopeFromKey(effScopeKey), campaignKey, campaignYear }) })
        : fosFunds.mutate({ operation: 'pledge.create', donorId: ledger.donor.id, scope: scopeFromKey(effScopeKey), campaignKey, campaignYear, amountCents: cents })));
      onSaved();
    } catch (cause) { setError(fosError(cause).message); } finally { setBusy(false); }
  };

  if (scopes.length === 0) return <p className="fos-hint"><Icon name="lock" size={13} /> You cannot record {isGift ? 'gifts' : 'pledges'} for this donor.</p>;
  return (
    <div className="fos-form">
      {isGift && (
        <label>Applies to
          <select value={pledgeId} onChange={(e) => setPledgeId(e.target.value)}>
            <option value="">A gift with no pledge</option>
            {openPledges.map((p) => <option key={p.id} value={p.id}>{p.ref} · {p.campaignName} {p.campaignYear} · {formatMoney(p.outstandingCents)} remaining</option>)}
          </select>
        </label>
      )}
      {pledge ? (
        <p className="fos-hint">Credited to <strong>{pledge.scopeLabel}</strong> for {pledge.campaignName} {pledge.campaignYear}, like the pledge.</p>
      ) : (
        <>
          <label>Credited to
            <select value={scopeKey} onChange={(e) => setScopeKey(e.target.value)}>{scopes.map((s) => <option key={s.scopeKey} value={s.scopeKey}>{s.label}</option>)}</select>
            <small className="fos-hint">Where the money counts toward goals.</small>
          </label>
          <div className="fos-form-row">
            <label>Campaign<select value={campaignKey} onChange={(e) => setCampaignKey(e.target.value)}>{ledger.campaigns.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}</select></label>
            <label>Year<select value={campaignYear} onChange={(e) => setCampaignYear(e.target.value)}>{openYears.map((y) => <option key={y} value={y}>{y}</option>)}</select></label>
          </div>
        </>
      )}
      <label>Amount<div className="fos-money-input"><span>$</span><input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" /></div></label>
      {isGift && <label>Date received<input type="date" value={received} max={todayLocal()} onChange={(e) => setReceived(e.target.value)} /></label>}
      {isGift && ledger.canConfirm && <label className="fos-check"><input type="checkbox" checked={confirmNow} onChange={(e) => setConfirmNow(e.target.checked)} /> I verified this against the council's records — record it as confirmed</label>}
      {isGift && !ledger.canConfirm && <p className="fos-hint">A gift you record is reported first. The council confirms it before it counts toward goals.</p>}
      <ErrorNote message={error} />
      <div className="drawer-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={submit} disabled={busy}>{busy ? <Spinner /> : isGift ? 'Record gift' : 'Record pledge'}</button></div>
      {modal}
    </div>
  );
}

/** Record a pledge or a gift. Given a donor it goes straight to the form; otherwise it first asks which donor. */
export default function ContributionDrawer({ mode, donor, ledger: known = null, onClose, onSaved }: { mode: ContributionMode; donor: { id: string; name: string } | null; ledger?: LedgerView | null; onClose: () => void; onSaved: () => void }) {
  const [picked, setPicked] = useState(donor);
  const [ledger, setLedger] = useState<LedgerView | null>(known);
  const [error, setError] = useState('');
  const { guard, modal } = useFinancialGuard();
  useEffect(() => {
    if (!picked || (known && known.donor.id === picked.id)) return;
    let cancelled = false; setLedger(null); setError('');
    guard(() => fosFunds.ledger({ donorId: picked.id })).then((l) => { if (!cancelled) setLedger(l); }).catch((cause) => { if (!cancelled) setError(fosError(cause).message); });
    return () => { cancelled = true; };
  }, [picked]); // eslint-disable-line react-hooks/exhaustive-deps
  const title = mode.kind === 'gift' ? 'Record a gift' : 'Record a pledge';
  return (
    <Drawer title={title} subtitle={picked ? picked.name : 'Choose a donor'} onClose={onClose}>
      {!picked && <DonorPicker onPick={setPicked} />}
      {picked && !ledger && !error && <Spinner label="Loading" />}
      <ErrorNote message={error} />
      {picked && ledger && <Form ledger={ledger} mode={mode} onClose={onClose} onSaved={onSaved} />}
      {picked && !donor && <p className="fos-fine"><button className="linkbtn" onClick={() => { setPicked(null); setLedger(null); }}>Choose a different donor</button></p>}
      {modal}
    </Drawer>
  );
}
